import type { Messages } from '../catalogue.en'

/**
 * Tasks: the personal and Patient work list, its editor and reminders.
 *
 * Every refusal the Task API can answer with gets its own sentence, because a
 * generic failure is what makes a clinician reload instead of act. The SMTP
 * acceptance is deliberately distinct from final delivery.
 *
 * The `…Many` counts read `Задач: {count}` rather than `{count} задач`. See
 * the note in `ru/common.ts`: Russian agrees the noun with the number, and
 * `{count} задач` is wrong for every count from 2 to 4.
 *
 * Composed into the public catalogue by `catalogue.ru.ts`. Nothing imports
 * this module directly: consumers ask the catalogue, so how the messages are
 * stored stays this layer's business.
 */
export const tasks: Partial<Messages> = {
  'task.title': 'Задачи',
  'task.loading': 'Загрузка задач',
  'task.loadFailed': 'Не удалось загрузить задачи.',
  'task.empty': 'Задач пока нет',
  'task.emptyDetail': 'Здесь появляются задачи, созданные вами или коллегой.',
  'task.filteredEmpty': 'Нет задач, соответствующих этим фильтрам',
  'task.filteredEmptyDetail': 'Измените или сбросьте фильтры, чтобы увидеть больше.',
  'task.new': 'Новая задача',
  'task.newForEvent': 'Новая задача',
  'task.editHeading': 'Изменить задачу',
  'task.editNamed': 'Изменить: {title}',
  'task.save': 'Сохранить задачу',
  'task.create': 'Создать задачу',
  'task.complete': 'Завершить',
  'task.completeNamed': 'Завершить: {title}',
  'task.cancelTask': 'Отменить задачу',
  'task.fieldTitle': 'Заголовок',
  'task.details': 'Подробности (необязательно)',
  'task.noDetails': 'Без подробностей',
  'task.dueAt': 'Срок',
  'task.noDue': 'Срок не задан',
  'task.dueOverdue': 'Просрочено',
  'task.dueToday': 'Срок сегодня',
  'task.remindAt': 'Напомнить (необязательно)',
  'task.repeat': 'Повтор',
  'task.repeat.none': 'Не повторяется',
  'task.repeat.daily': 'Ежедневно',
  'task.repeat.weekly': 'Еженедельно',
  'task.repeat.monthly': 'Ежемесячно',
  'task.priority': 'Приоритет',
  'task.priority.low': 'Низкий',
  'task.priority.normal': 'Обычный',
  'task.priority.high': 'Высокий',
  'task.status.open': 'Открыта',
  'task.status.inProgress': 'В работе',
  'task.status.completed': 'Завершена',
  'task.status.cancelled': 'Отменена',
  'task.assignee': 'Ответственный врач',
  'task.patient': 'Пациент (необязательно)',
  'task.scheduleEvent': 'Событие расписания (необязательно)',
  'task.noPatient': 'Без пациента',
  'task.noScheduleEvent': 'Без события расписания',
  'task.linkedScheduleEvent': 'Связанное событие расписания · {date}',
  'task.notAnAppointment': 'Задача на событии расписания — это не приём: она не резервирует время и не занимает доступность.',
  'task.unsavedIntent': 'Задача \u2014 правки, которые не были сохранены',
  'task.notify': 'Отправить напоминания об этой задаче',
  'task.notifyDetail': 'Задача может существовать без единого сообщения. Включайте это только тогда, когда кому-то нужно напомнить.',
  'task.messageText': 'Текст напоминания',
  'task.recipients': 'Получатели',
  'task.recipientsHint': 'Удерживайте Ctrl или Cmd, чтобы выбрать несколько.',
  'task.titleRequired': 'Нужен заголовок.',
  'task.assigneeRequired': 'Нужен один ответственный врач.',
  'task.messageIncomplete': 'Для напоминания нужны текст и хотя бы один получатель.',
  'task.contextMismatch': 'Это событие расписания относится к другому пациенту.',
  'task.contextEventOption': '{event}, {patient}',
  'task.staleRevision': 'Эта задача изменилась в другом месте. Ваш текст сохранён; перезагрузите её, чтобы увидеть текущую редакцию перед сохранением.',
  'task.notActive': 'Эта задача завершена или отменена и больше не может изменяться.',
  'task.rejected': 'Задача отклонена в том виде, как она введена. Проверьте поля и попробуйте ещё раз.',
  'task.gone': 'Эта задача больше недоступна.',
  'task.saveFailed': 'Не удалось сохранить задачу. Попробуйте ещё раз.',
  'task.tableLabel': 'Задачи',
  'task.columnTitle': 'Задача',
  'task.columnStatus': 'Статус',
  'task.columnPriority': 'Приоритет',
  'task.columnDue': 'Срок',
  'task.columnAssignee': 'Ответственный',
  'task.columnPatient': 'Пациент',
  'task.columnActions': 'Действия',
  'task.filterAssignedToMe': 'Назначен мне',
  'task.patientNoMatches': 'Нет подходящих пациентов',
  'task.start': 'Начать работу',
  'task.returnToOpen': 'Вернуть в открытые',
  'task.scheduleContext': 'Событие расписания',
  'task.resultCountOne': '{count} задача',
  'task.resultCountMany': 'Задач: {count}',
  'task.simulate': 'Смоделировать сейчас',
  'task.simulateNamed': 'Смоделировать сейчас: {title}',
  'task.simulating': 'Моделирование…',
  'task.simulateExplains': 'Это запускает выбранные каналы как СИМУЛЯЦИЮ. Ничего не отправляется, ни один провайдер не вызывается.',
  'task.simulationNotConfigured': 'Для этой задачи не настроено напоминание, поэтому моделировать нечего.',
  'task.simulationConflict': 'Эта симуляция уже записана.',
  'task.reminderConfigured': 'Напоминание настроено',
  'task.routingPreferences': 'Доставка определяется настройками связи каждого получателя.',
  'task.attempts': 'Попытки напоминания',
  'task.attemptsNamed': 'Попытки напоминания: {title}',
  'task.attemptsEmpty': 'Попыток пока нет',
  'task.attemptsFailed': 'Не удалось загрузить попытки напоминания.',
  'task.attemptTrigger.manual': 'Вручную',
  'task.attemptTrigger.automatic': 'Автоматически',
  'task.remindersTitle': 'Напоминания на сегодня',
  'task.remindersEmpty': 'В этот день напоминаний нет',
  'task.scheduleCountOne': '{count} задача',
  'task.scheduleCountMany': 'Задач: {count}',
  'task.scheduleRowTasksOne': '{count} задача для {event}',
  'task.scheduleRowTasksMany': 'Задач для {event}: {count}',
  'task.scheduleUnknown': 'Задачи недоступны',
  'task.scheduleLoadFailed': 'Не удалось загрузить напоминания. Приёмы выше не затронуты.',
  'task.openTask': 'Открыть задачу {title}',
}
